import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import {
  ExButton,
  ExCheck,
  ExInput,
  RULES_PATTERN_EMAIL,
  RULES_PATTERN_PASSWORD,
  RULES_PATTERN_PHONE_NUMBER,
  RULES_PHONE_NUMBER_MAX_LENGTH,
  useResizeObserver,
} from "@energyx-dev/energyx-ui";
import { useMutation } from "@tanstack/react-query";
import { postCorporationLogin, postLogin } from "api/login";
import { DEVICE } from "constants/device";
import {
  EN_RULES_PATTERN_EMAIL,
  EN_RULES_PATTERN_PASSWORD,
  EN_RULES_PATTERN_PHONE_NUMBER,
  EN_RULES_PHONE_NUMBER_MAX_LENGTH,
  EN_RULES_REQUIERD,
  RULES_REQUIERD,
} from "constants/rules";
import { hackleEventUtil } from "hackle/hackleEventUtil";
import { hackleClient } from "hackle/hackleInstance";
import useTranslation from "hooks/useTranslation";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { TLoginReq } from "types/auth/authTypes";
import { TCustomError } from "types/commonTypes";
import { THackleMemberTypeProps } from "types/hackle";
import { removeHyphen } from "utils/formatters";
import { getStorage, removeStorage, setLocalStorage } from "utils/localStorage";

export const LoginForm = ({
  tabIndex,
  urlParameter,
}: {
  tabIndex: number;
  urlParameter: string;
}) => {
  const theme = useTheme();
  const device = useResizeObserver();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const callbackUrl = searchParams.get("callbackUrl");
  const isEnglishPage = useTranslation();

  const { setValue, getValues, setError, clearErrors, trigger } = useFormContext();

  const inputSize = device === DEVICE.DESKTOP ? "desktop" : "mobile";
  const hackleMemberProps = { member_type: !tabIndex ? "일반" : "법인" } satisfies {
    member_type: "일반" | "법인";
  };
  const { mutate: loginMutate } = useMutation(!tabIndex ? postLogin : postCorporationLogin);

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onSubmit();
  };

  const checkCallbackUrlIsEnergyxDomain = () => {
    if (!callbackUrl) return false;

    const energyxDomainArr = [
      process.env.ENERGYX_URL,
      process.env.STORE_URL,
      process.env.TOMARCH_URL,
      process.env.DESIGN_WHOS_URL,
    ];

    return energyxDomainArr.some((domain) => callbackUrl.includes(domain ?? ""));
  };

  const onSubmit = async () => {
    if (!tabIndex && !getValues("save")) {
      removeStorage("phoneNumber");
    }
    if (tabIndex && !getValues("corporationSave")) {
      removeStorage("email");
    }

    const params: TLoginReq = {
      loginId: !tabIndex ? removeHyphen(getValues("phoneNumber")) : getValues("email"),
      password: getValues("password"),
    };

    loginMutate(params, {
      onSuccess: () => {
        clearErrors();

        if (!tabIndex && getValues("save")) {
          setLocalStorage("phoneNumber", getValues("phoneNumber"));
        }
        if (tabIndex && getValues("corporationSave")) {
          setLocalStorage("email", getValues("email"));
        }
        hackleClient.track({
          key: "complete_Login",
          properties: {
            SiteName: callbackUrl || urlParameter || process.env.ENERGYX_URL,
            User_Type: tabIndex === 0 ? "일반" : "법인",
          },
        });
        if (checkCallbackUrlIsEnergyxDomain()) {
          return (window.location.href = `${process.env.API_URL}/sso/login?redirectUrl=${callbackUrl}&continueUrl=${window.location.origin}`);
        } else {
          window.location.href = `${process.env.API_URL}/sso/login?redirectUrl=${
            urlParameter || process.env.ENERGYX_URL
          }&continueUrl=${window.location.origin}`;
        }
      },
      onError: (error) => {
        const _err = error as TCustomError;
        const status = _err.response?.status;

        clearErrors();

        if (status === 400) {
          setError("password", {
            type: "custom",
            message: isEnglishPage ? "Please confirm your password." : "비밀번호를 확인해주세요.",
          });

          return;
        }

        if (status === 404) {
          const errorMessage =
            tabIndex === 0
              ? isEnglishPage
                ? "Please confirm your mobile phone number."
                : "휴대폰 번호를 확인해주세요."
              : isEnglishPage
              ? "Please confirm your e-mail address."
              : "이메일 주소를 확인해주세요.";
          const fieldToSetError = tabIndex === 0 ? "phoneNumber" : "email";

          setError(fieldToSetError, {
            type: "custom",
            message: errorMessage,
          });
        }
      },
    });
  };

  const handleOnBlurInput = async (type: "phoneNumber" | "email" | "password") => {
    if (!(await trigger([type]))) return;
    hackleEventUtil<THackleMemberTypeProps>({
      key: type === "phoneNumber" || type === "email" ? "blur_login_id" : "blur_login_password",
      properties: hackleMemberProps,
    });
  };

  const handleOnChangeSaveId = (isChecked: boolean) => {
    if (isChecked) {
      hackleEventUtil<THackleMemberTypeProps>({
        key: "check_login_save_id",
        properties: hackleMemberProps,
      });
    }
  };

  useEffect(() => {
    const storageKey = !tabIndex ? "phoneNumber" : "email";
    const saveValue = !tabIndex ? "save" : "corporationSave";

    const storedValue = getStorage(storageKey);
    if (storedValue) {
      setValue(storageKey, storedValue);
      setValue(saveValue, true);
    }
  }, [tabIndex, setValue]);

  return (
    <LoginFormContainer>
      <div>
        {tabIndex === 0 && (
          <ExInput
            type={"input"}
            name={"phoneNumber"}
            password={false}
            inputSize={inputSize}
            label={isEnglishPage ? "Mobile Phone" : "휴대폰 번호"}
            placeholder={isEnglishPage ? "Mobile Phone" : "휴대폰 번호"}
            phoneNumAutoHyphen={true}
            callbackOnBlur={() => handleOnBlurInput("phoneNumber")}
            callbackOnInputKeyDown={(e) => handleOnKeyDown(e)}
            rules={
              isEnglishPage
                ? {
                    ...EN_RULES_REQUIERD,
                    ...EN_RULES_PATTERN_PHONE_NUMBER,
                    ...EN_RULES_PHONE_NUMBER_MAX_LENGTH,
                  }
                : {
                    ...RULES_REQUIERD,
                    ...RULES_PATTERN_PHONE_NUMBER,
                    ...RULES_PHONE_NUMBER_MAX_LENGTH,
                  }
            }
          />
        )}
        {tabIndex === 1 && (
          <ExInput
            type={"input"}
            name={"email"}
            inputSize={inputSize}
            label={isEnglishPage ? "E-mail" : "이메일 주소"}
            placeholder={isEnglishPage ? "E-mail" : "이메일 주소"}
            callbackOnBlur={() => handleOnBlurInput("email")}
            callbackOnInputKeyDown={(e) => handleOnKeyDown(e)}
            rules={
              isEnglishPage
                ? {
                    ...EN_RULES_REQUIERD,
                    ...EN_RULES_PATTERN_EMAIL,
                  }
                : {
                    ...RULES_REQUIERD,
                    ...RULES_PATTERN_EMAIL,
                  }
            }
          />
        )}
        <ExInput
          type={"input"}
          name={"password"}
          password
          inputSize={inputSize}
          label={isEnglishPage ? "Password" : "비밀번호"}
          placeholder={isEnglishPage ? "Password" : "비밀번호"}
          callbackOnBlur={() => handleOnBlurInput("password")}
          callbackOnInputKeyDown={(e) => handleOnKeyDown(e)}
          rules={
            isEnglishPage
              ? {
                  ...EN_RULES_REQUIERD,
                  ...EN_RULES_PATTERN_PASSWORD,
                }
              : {
                  ...RULES_REQUIERD,
                  ...RULES_PATTERN_PASSWORD,
                }
          }
        />
      </div>
      <div>
        <div>
          {tabIndex === 0 && (
            <ExCheck
              name={"save"}
              label={isEnglishPage ? "Remember ID" : "아이디 저장"}
              size={"small"}
              callbackOnChange={handleOnChangeSaveId}
            />
          )}
          {tabIndex === 1 && (
            <ExCheck
              name={"corporationSave"}
              label={isEnglishPage ? "Remember ID" : "아이디 저장"}
              size={"small"}
            />
          )}
        </div>
        <ExButton
          variant={"contents"}
          size={"tiny"}
          label={isEnglishPage ? "Find Password" : "비밀번호 찾기"}
          labelColor={theme.color.mono[60]}
          onClick={() => {
            hackleEventUtil<THackleMemberTypeProps>({
              key: "click_login_find_password",
              properties: hackleMemberProps,
            });

            if (isEnglishPage) {
              navigate(`/find-password?callbackUrl=${callbackUrl}&lang=en`);
              return;
            }
            navigate(`/find-password${callbackUrl ? `?callbackUrl=${callbackUrl}` : ""}`);
          }}
        />
      </div>
      <ExButton
        variant={"contained"}
        size={device === DEVICE.DESKTOP ? "xLarge" : "large"}
        label={isEnglishPage ? "Sign In" : "로그인"}
        backgroundColor={isEnglishPage ? "#2F2F30" : ""}
        onClick={onSubmit}
      />
    </LoginFormContainer>
  );
};

const LoginFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  > div:first-of-type {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  > div:nth-of-type(2) {
    display: flex;
    gap: 32px;
    align-items: center;

    > div:first-of-type {
      flex: 1;
    }
  }
`;
